/**
 * Modal d'édition manuelle du contenu (markdown) d'un document.
 * Utilisé pour les documents ODS / Offre de service dans Livrables créés.
 */

import { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';

interface DocumentManualEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  content: string;
  documentTitle: string;
  onSave: (newContent: string) => Promise<void>;
}

export default function DocumentManualEditModal({
  isOpen,
  onClose,
  content,
  documentTitle,
  onSave,
}: DocumentManualEditModalProps) {
  const [value, setValue] = useState(content);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setValue(content);
      setError(null);
    }
  }, [isOpen, content]);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await onSave(value);
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-bold text-gray-900">Modifier le texte manuellement – {documentTitle}</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-hidden flex flex-col p-4">
          <p className="text-xs text-gray-500 mb-2">
            Vous pouvez modifier le contenu ci-dessous (format Markdown). Les modifications seront enregistrées telles quelles.
          </p>
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="flex-1 w-full min-h-[400px] p-4 border border-gray-300 rounded-lg font-mono text-sm resize-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Contenu du document (Markdown)"
            spellCheck="false"
          />
          {error && (
            <div className="mt-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg bg-white font-medium hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}
